/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */
import React, { useState, useEffect } from "react";
import BrandList from "../components/ProductBrandList";
import {
  searchProductBrands, addProductBrand, deleteProductBrand, editProductBrand,
} from "../../../api/productBrandServices";
import { hasAccessRights } from "../../../common/security/PermissionUtils";
import CommonUtils from "../../../common/utils/CommonUtils";
import Error from "../../404/containers/ErrorContainer";
import validateForm from "../../../common/utils/validateForm";

const ProductBrandContainer = () => {
  const availablePageSizes = [20, 50, 100];
  const [brandList, setBrandList] = useState([]);
  const [totalBrandCount, setTotalBrandCount] = useState([]);
  const [selectedPage, setSelectedPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPageSize, setSelectedPageSize] = useState(20);
  const [isLoading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteProductBrandInProgress, setDeleteProductBrandInProgress] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [message, setMessage] = useState({
    type: null,
    message: "",
  });
  const [addModal, setAddModal] = useState(false);
  const [modalContents, setModalContents] = useState({
    id: "",
    brandId: "",
    brandName: "",
  });

  const methods = validateForm();
  const {
    register, errors, handleSubmit, clearErrors,
  } = methods;
  const [isSaveInProgress, setSaveInProgressFlag] = useState(false);
  const getBrandsFromServer = (searchValue = "", page = 0, pageSize = 20) => {
    setLoading(true);
    searchProductBrands(searchValue, page, pageSize).then((response) => {
      setLoading(false);
      if (response && response.success && response.data) {
        const { totalCount, brands } = response.data;
        if (CommonUtils.isListNotEmpty(brands) && totalCount > 0) {
          setBrandList(brands);
          setTotalBrandCount(totalCount);
        } else {
          setBrandList([]);
          setTotalBrandCount(0);
        }
      } else {
        setBrandList([]);
        setTotalBrandCount(0);
      }
    });
  };
  const handleSearchTermChange = (e) => {
    const { value } = e.target;
    setSearchTerm(value);
  };

  const handleSearchButtonClick = () => {
    setSelectedPage(0);
    getBrandsFromServer(searchTerm, 0, selectedPageSize);
  };
  const handlePageChange = (newPage) => {
    setSelectedPage(newPage);
    getBrandsFromServer(searchTerm, newPage, selectedPageSize);
  };

  const handlePageSizeChange = (newPageSize) => {
    setSelectedPageSize(newPageSize);
    setSelectedPage(0);
    getBrandsFromServer(searchTerm, 0, newPageSize);
  };

  const handleClearModalContents = () => {
    setModalContents({
      brandId: "",
      brandName: "",
      id: "",
    });
  };

  const toggleAddModal = () => {
    handleClearModalContents();
    setAddModal(!addModal);
    clearErrors("brandId");
    clearErrors("brandName");
    setMessage({ type: null, message: "" });
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    setModalContents({
      ...modalContents,
      [name]: value,
    });
  };

  const submitAddBrandForm = () => {
    const { brandId, brandName } = modalContents;
    const requestBody = { brandId, brandName };
    setSaveInProgressFlag(true);
    addProductBrand(requestBody).then((response) => {
      setSaveInProgressFlag(false);
      if (response && response.success && response.data) {
        handleClearModalContents();
        setAddModal(false);
        setMessage({ type: "success", message: response.messages[0] });
        getBrandsFromServer();
        setTimeout(() => {
          setMessage({ type: null, message: "" });
        }, 3000);
      } else if (response && response.success === false
        && CommonUtils.isListNotEmpty(response.messages)) {
        setMessage({ type: "warning", message: response.messages[0] });
        setTimeout(() => {
          setMessage({ type: null, message: "" });
        }, 3000);
      } else {
        setMessage({ type: "warning", message: "Something went wrong" });
        setTimeout(() => {
          setMessage({ type: null, message: "" });
          setAddModal(false);
          getBrandsFromServer();
        }, 3000);
      }
    });
  };

  const handleDeleteConfirmationModal = (brandData) => {
    if (deleteModal) {
      setModalContents({ id: "", brandId: "", brandName: "" });
    } else {
      setModalContents(brandData);
    }
    setDeleteModal(!deleteModal);
  };

  /**
*  This function is to delete a Brand
*/

  const handleDeleteProductBrand = (id) => {
    deleteProductBrand(id).then((response) => {
      if (response && response.success) {
        setDeleteProductBrandInProgress(true);
        setDeleteModal(!deleteModal);
        setMessage({ type: "success", message: response.messages[0] });
        getBrandsFromServer();
        setTimeout(() => {
          setMessage({ type: null, message: "" });
          setDeleteProductBrandInProgress(false);
        }, 3000);
      } else {
        setMessage({ type: "warning", message: "Something went wrong" });
        setTimeout(() => {
          setMessage({ type: null, message: "" });
          setDeleteModal(!deleteModal);
          getBrandsFromServer();
        }, 3000);
      }
    });
  };
  const submitEditBrandForm = () => {
    const { brandId, brandName } = modalContents;
    const requestBody = { brandId, brandName };
    setSaveInProgressFlag(true);
    editProductBrand(modalContents.id, requestBody).then((response) => {
      setSaveInProgressFlag(false);
      if (response && response.success && response.data) {
        handleClearModalContents();
        setEditModal(false);
        setMessage({ type: "success", message: response.messages[0] });
        getBrandsFromServer();
        setTimeout(() => {
          setMessage({ type: null, message: "" });
        }, 3000);
      } else if (response && response.success === false
        && CommonUtils.isListNotEmpty(response.messages)) {
        setMessage({ type: "warning", message: response.messages[0] });
        setTimeout(() => {
          setMessage({ type: null, message: "" });
        }, 3000);
      } else {
        setMessage({ type: "warning", message: "Something went wrong" });
        setTimeout(() => {
          setMessage({ type: null, message: "" });
          setAddModal(false);
          getBrandsFromServer();
        }, 3000);
      }
    });
  };

  useEffect(() => {
    getBrandsFromServer();
  }, []);
  const toggleEditModal = (brandData) => {
    if (editModal) {
      setModalContents({ id: "", brandName: "", brandId: "" });
    } else {
      setModalContents(brandData);
    }
    setEditModal(!editModal);
  };
  return (
    hasAccessRights(["ViewProductBrandsLink"])
      ? (
        <BrandList
          brandList={brandList}
          totalBrandCount={totalBrandCount}
          availablePageSizes={availablePageSizes}
          selectedPage={selectedPage}
          selectedPageSize={selectedPageSize}
          searchTerm={searchTerm}
          handleSearchTermChange={handleSearchTermChange}
          handleSearchButtonClick={handleSearchButtonClick}
          handlePageChange={handlePageChange}
          handlePageSizeChange={handlePageSizeChange}
          isLoading={isLoading}
          submitAddBrandForm={submitAddBrandForm}
          handleSubmit={handleSubmit}
          addModal={addModal}
          modalContents={modalContents}
          toggleAddModal={toggleAddModal}
          handleChange={handleChange}
          register={register}
          errors={errors}
          message={message}
          isSaveInProgress={isSaveInProgress}
          toggleEditModal={toggleEditModal}
          editModal={editModal}
          submitEditBrandForm={submitEditBrandForm}
          handleDeleteConfirmationModal={handleDeleteConfirmationModal}
          deleteModal={deleteModal}
          handleDeleteProductBrand={handleDeleteProductBrand}
          deleteProductBrandInProgress={deleteProductBrandInProgress}
        />
      )
      : (<Error />)
  );
};

export default ProductBrandContainer;
